import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import VerifiedIcon from '@mui/icons-material/Verified';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useAuth } from '../auth/AuthContext';
import { getApiErrorMessage, resolveApiAssetUrl } from '../api/client';
import { DEFAULT_TITLE, usePortalBranding } from '../context/PortalContext';
import { GoogleSignInButton } from '../components/GoogleSignInButton';
import { PortalLogo } from '../components/PortalLogo';
import { loginLogoHeight } from '../components/portalLogoSize';

interface LoginForm {
  identifier: string;
  password: string;
}

interface LocationState {
  from?: string;
}

export function Login(): JSX.Element {
  const { login, loginWithGoogle } = useAuth();
  const { branding } = usePortalBranding();
  const logoSrc = resolveApiAssetUrl(branding?.logo_url);
  const navigate = useNavigate();
  const location = useLocation();
  const [serverError, setServerError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginForm>({ defaultValues: { identifier: '', password: '' } });

  const onSubmit = async (data: LoginForm) => {
    setServerError(null);
    try {
      await login(data.identifier, data.password);
      const from = (location.state as LocationState | null)?.from ?? '/dashboard';
      navigate(from, { replace: true });
    } catch (err) {
      setServerError(getApiErrorMessage(err, 'Tài khoản hoặc mật khẩu không đúng.'));
    }
  };

  const onGoogleCredential = async (credential: string) => {
    setServerError(null);
    try {
      await loginWithGoogle(credential);
      const from = (location.state as LocationState | null)?.from ?? '/dashboard';
      navigate(from, { replace: true });
    } catch (err) {
      // PENDING_APPROVAL có màn riêng — các lỗi khác hiện tại chỗ.
      const code = (err as { response?: { data?: { error?: string } } }).response?.data?.error;
      if (code === 'PENDING_APPROVAL') {
        navigate('/pending-approval', { replace: true });
        return;
      }
      setServerError(getApiErrorMessage(err, 'Không đăng nhập được bằng Google.'));
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        p: 2,
      }}
    >
      {/* overflow hidden để dải logo tràn sát mép mà vẫn bị cắt theo bo góc thẻ. */}
      <Paper
        elevation={3}
        sx={{ width: '100%', maxWidth: 420, borderRadius: 3, overflow: 'hidden' }}
      >
        {logoSrc && (
          <PortalLogo
            src={logoSrc}
            alt={branding?.display_name ?? ''}
            height={loginLogoHeight(branding?.logo_height)}
            onDark={Boolean(branding?.logo_on_dark)}
          />
        )}
        <Box sx={{ p: 4, pt: logoSrc ? 3 : 4 }}>
          <Stack spacing={1} alignItems="center" sx={{ mb: 3 }}>
            {!logoSrc && <VerifiedIcon color="primary" sx={{ fontSize: 44 }} />}
            <Typography variant="h5" fontWeight={700} textAlign="center">
              {branding?.display_name ?? DEFAULT_TITLE}
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Đăng nhập để quản lý dự án &amp; nghiệm thu tính năng
            </Typography>
          </Stack>

          {serverError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {serverError}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
            <Stack spacing={2.5}>
              <TextField
                label="Email hoặc tên đăng nhập"
                type="text"
                fullWidth
                autoComplete="username"
                autoFocus
                error={Boolean(errors.identifier)}
                helperText={errors.identifier?.message}
                {...register('identifier', {
                  required: 'Vui lòng nhập email hoặc tên đăng nhập',
                })}
              />
              <TextField
                label="Mật khẩu"
                type={showPassword ? 'text' : 'password'}
                fullWidth
                autoComplete="current-password"
                error={Boolean(errors.password)}
                helperText={errors.password?.message}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={showPassword ? 'Ẩn mật khẩu' : 'Hiện mật khẩu'}
                          onClick={() => setShowPassword((prev) => !prev)}
                          edge="end"
                          tabIndex={-1}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
                {...register('password', {
                  required: 'Vui lòng nhập mật khẩu',
                  minLength: { value: 6, message: 'Mật khẩu tối thiểu 6 ký tự' },
                })}
              />
              <Button
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Đang đăng nhập…' : 'Đăng nhập'}
              </Button>
            </Stack>
          </Box>

          <GoogleSignInButton onCredential={(c) => void onGoogleCredential(c)} />

          {branding?.project_id && (
            <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 3 }}>
              <Button size="small" href={`/gioi-thieu/${branding.project_id}`} target="_blank">
                Xem giới thiệu
              </Button>
              <Button size="small" href={`/huong-dan/${branding.project_id}`} target="_blank">
                Xem hướng dẫn sử dụng
              </Button>
            </Stack>
          )}
        </Box>
      </Paper>
    </Box>
  );
}
